import type { CSSProperties } from 'react'
import { useCallback, useContext, useMemo, useRef, useState } from 'react'
import BodyText from 'LuxKit/Typography/BodyText'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import { MAPBOX_STATIC_IMAGE_MAX_DIMENSION } from 'components/Common/PropertyMap/constants'
import Group from 'components/utils/Group'
import loadable from 'components/utils/lazyImport'
import ModalContext from 'contexts/ModalContext'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useElementBoundingClientRectObserver } from 'hooks/useElementSize'
import LineArrowUpRightIcon from 'LuxKit/Icons/line/LineArrowUpRightIcon'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineCarIcon from 'LuxKit/Icons/line/LineCarIcon'
import getOfferFeatureIcon from 'lib/offer/getOfferFeatureIcon'
import { getMapboxAccessToken, MAPBOX_ACCESS_TOKEN_USE_CASES } from 'lib/maps/mapboxAccessToken'
import { rem } from 'polished'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'
import {
  buildExperiencesPassportStaticMapUrl,
  getExperiencesPassportMapViewport,
  projectCoordinatesToExperiencesPassportMap,
} from './experiencesPassportMapUtils'
import type { ExperiencesPassportInclusion } from './experiencesPassportTypes'

const MapCard = styled.div`
  overflow: hidden;
  border: ${rem(1)} solid ${props => props.theme.palette.neutral.default.five};
  border-radius: ${rem(20)};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const MapPreview = styled.div`
  position: relative;
  height: ${rem(150)};
  overflow: hidden;
  isolation: isolate;

`

const RouteMapImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const MapRouteSvg = styled.svg`
  position: absolute;
  inset: 0;
  z-index: 1;
  width: 100%;
  height: 100%;
  pointer-events: none;

  .route {
    fill: none;
    stroke: #2159CA;
    stroke-dasharray: 4 4;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-width: 3;
    vector-effect: non-scaling-stroke;
  }
`

const MapPin = styled.div`
  position: absolute;
  z-index: 3;
  display: grid;
  place-items: center;
  width: ${rem(28)};
  height: ${rem(28)};
  border: ${rem(2)} solid ${props => props.theme.palette.neutral.surface.normalBackground};
  border-radius: 50%;
  color: ${props => props.theme.palette.neutral.surface.normalBackground};
  background-color: ${props => props.theme.palette.messaging.critical.normalForeground};
  box-shadow: ${props => props.theme.shadow.bottom.small};
  transform: translate(-50%, -50%);
`

const MapLabel = styled(BodyText)`
  position: absolute;
  z-index: 4;
  max-width: ${rem(152)};
  padding: ${rem(3)} ${rem(8)};
  border-radius: ${rem(999)};
  color: ${props => props.theme.palette.neutral.default.one};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  box-shadow: ${props => props.theme.shadow.bottom.small};

  &.origin {
    transform: translate(${rem(20)}, calc(-100% - ${rem(6)}));
  }

  &.destination {
    transform: translate(calc(-100% - ${rem(20)}), ${rem(4)});
  }
`

const DrivingBadge = styled(BodyText)`
  position: absolute;
  z-index: 3;
  top: 50%;
  inset-inline-start: 50%;
  display: inline-flex;
  align-items: center;
  gap: ${rem(2)};
  padding: ${rem(3)} ${rem(7)};
  border-radius: ${rem(999)};
  color: ${props => props.theme.palette.neutral.surface.normalBackground};
  background-color: ${props => props.theme.palette.brand.primary.normal};
  box-shadow: ${props => props.theme.shadow.bottom.small};
  transform: translate(-50%, -50%);
`

const MapFooter = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: ${rem(12)};
  align-items: center;
  padding: ${rem(8)} ${rem(12)};
`

const MapButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${rem(4)};
  height: ${rem(28)};
  padding: 0 ${rem(8)};
  border: ${rem(1)} solid ${props => props.theme.palette.neutral.default.four};
  border-radius: ${rem(999)};
  color: ${props => props.theme.palette.neutral.default.one};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const ModalMapBody = styled(ModalBody)`
  min-height: 0;
`

const ModalMapContent = styled(ModalContent)`
  display: flex;
  min-height: 0;
  padding: 0;
`

const ModalMapContainer = styled.div`
  flex: 1;
  min-height: 0;
  height: 100%;
`

const STATIC_VIEWPORT_PADDING = 16

interface Props {
  inclusion: ExperiencesPassportInclusion
}

interface RouteMapCanvasProps {
  inclusion: ExperiencesPassportInclusion
  mapHeight: number
  mapMarkers: Array<App.MapMarker>
  staticImageWidth: number
}

type InteractiveRouteMapProps = Pick<RouteMapCanvasProps, 'inclusion'>

const ExperiencesPassportInteractiveRouteMap = loadable<InteractiveRouteMapProps>(
  () => import('./ExperiencesPassportInteractiveRouteMap'),
)

interface MapSize {
  height: number
  width: number
}

interface MapPoint {
  x: number
  y: number
}

function getStaticMapSize(width: number, height: number): MapSize {
  const scale = Math.min(
    1,
    MAPBOX_STATIC_IMAGE_MAX_DIMENSION / width,
    MAPBOX_STATIC_IMAGE_MAX_DIMENSION / height,
  )

  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  }
}

function getMapPointStyle(point: MapPoint, mapSize: MapSize): CSSProperties {
  return {
    left: `${point.x / mapSize.width * 100}%`,
    top: `${point.y / mapSize.height * 100}%`,
  }
}

function RouteMapCanvas({ inclusion, mapHeight, mapMarkers, staticImageWidth }: RouteMapCanvasProps) {
  const DestinationIcon = getOfferFeatureIcon(inclusion.symbol)
  const mapPreviewRef = useRef<HTMLDivElement>(null)
  const [staticMapSize, setStaticMapSize] = useState(() => getStaticMapSize(staticImageWidth, mapHeight))
  const updateStaticMapSize = useCallback((rect: DOMRect) => {
    if (!rect.width || !rect.height) return

    const nextSize = getStaticMapSize(rect.width, rect.height)
    setStaticMapSize(currentSize => (
      currentSize.width === nextSize.width && currentSize.height === nextSize.height ? currentSize : nextSize
    ))
  }, [])
  useElementBoundingClientRectObserver(mapPreviewRef, updateStaticMapSize)

  const routeLayout = useMemo(() => {
    const origin = inclusion.mapOriginLocation
    const destination = inclusion.mapDestinationLocation
    if (!origin || !destination) return undefined

    const viewport = getExperiencesPassportMapViewport(mapMarkers, staticMapSize, STATIC_VIEWPORT_PADDING)
    if (!viewport) return undefined

    const originPoint = projectCoordinatesToExperiencesPassportMap(origin.latitude, origin.longitude, viewport, staticMapSize)
    const destinationPoint = projectCoordinatesToExperiencesPassportMap(destination.latitude, destination.longitude, viewport, staticMapSize)
    const deltaX = destinationPoint.x - originPoint.x
    const deltaY = destinationPoint.y - originPoint.y
    const controlPointOne = {
      x: originPoint.x + deltaX * 0.35,
      y: originPoint.y + deltaY * 0.2,
    }
    const controlPointTwo = {
      x: originPoint.x + deltaX * 0.65,
      y: originPoint.y + deltaY * 0.8,
    }

    return {
      destinationPoint,
      destinationStyle: getMapPointStyle(destinationPoint, staticMapSize),
      originPoint,
      originStyle: getMapPointStyle(originPoint, staticMapSize),
      path: `M ${originPoint.x} ${originPoint.y} C ${controlPointOne.x} ${controlPointOne.y}, ${controlPointTwo.x} ${controlPointTwo.y}, ${destinationPoint.x} ${destinationPoint.y}`,
      drivingBadgeStyle: getMapPointStyle({
        x: (originPoint.x + destinationPoint.x) / 2,
        y: (originPoint.y + destinationPoint.y) / 2,
      }, staticMapSize),
    }
  }, [inclusion.mapDestinationLocation, inclusion.mapOriginLocation, mapMarkers, staticMapSize])
  const staticMapUrl = useMemo(() => {
    const accessToken = getMapboxAccessToken(MAPBOX_ACCESS_TOKEN_USE_CASES.experiences)

    return accessToken ? buildExperiencesPassportStaticMapUrl(
      accessToken,
      mapMarkers,
      staticMapSize,
      STATIC_VIEWPORT_PADDING,
    ) : undefined
  }, [mapMarkers, staticMapSize])

  return <MapPreview ref={mapPreviewRef}>
    {!!staticMapUrl && <RouteMapImage alt="" src={staticMapUrl} loading="lazy" decoding="async" draggable={false} />}
    {!!routeLayout && <>
      <MapRouteSvg
        viewBox={`0 0 ${staticMapSize.width} ${staticMapSize.height}`}
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <path className="route" d={routeLayout.path} />
      </MapRouteSvg>
      <MapPin
        className="origin"
        style={routeLayout.originStyle}
        aria-hidden="true"
      >
        <LineBedIcon size="XS" />
      </MapPin>
      {!!inclusion.mapOriginText && <MapLabel
        className="origin"
        style={routeLayout.originStyle}
        variant="small"
        weight="bold"
        wrap="truncate"
      >
        {inclusion.mapOriginText}
      </MapLabel>}
      {!!inclusion.mapDrivingTimeText && <DrivingBadge
        style={routeLayout.drivingBadgeStyle}
        variant="small"
        weight="bold"
      >
        <LineCarIcon size="XXS" />
        {inclusion.mapDrivingTimeText}
      </DrivingBadge>}
      <MapPin
        className="destination"
        style={routeLayout.destinationStyle}
        aria-hidden="true"
      >
        <DestinationIcon size="XS" />
      </MapPin>
      {!!inclusion.mapDestinationText && <MapLabel
        className="destination"
        style={routeLayout.destinationStyle}
        variant="small"
        weight="bold"
        wrap="truncate"
      >
        {inclusion.mapDestinationText}
      </MapLabel>}
    </>}
  </MapPreview>
}

function ExperiencesPassportRouteMapModal({ inclusion }: Pick<RouteMapCanvasProps, 'inclusion'>) {
  const modalElementContext = useModalElementContext()
  const closeModal = useCallback(() => {
    modalElementContext.resolve()
  }, [modalElementContext])

  return <ModalBase size="max" height="full">
    <ModalHeader
      title={inclusion.mapRouteText ?? inclusion.title}
      dismissible={false}
      onBackButtonClick={closeModal}
    />
    <ModalMapBody>
      <ModalMapContent>
        <ModalMapContainer>
          <ExperiencesPassportInteractiveRouteMap
            inclusion={inclusion}
          />
        </ModalMapContainer>
      </ModalMapContent>
    </ModalMapBody>
  </ModalBase>
}

function ExperiencesPassportRouteMap({ inclusion }: Props) {
  const mapMarkers = useMemo(() => [
    ...(inclusion.mapOriginLocation ? [{
      id: 'experiences-passport-origin',
      type: 'property' as const,
      latitude: inclusion.mapOriginLocation.latitude,
      longitude: inclusion.mapOriginLocation.longitude,
    }] : []),
    ...(inclusion.mapDestinationLocation ? [{
      id: 'experiences-passport-destination',
      type: 'eat-and-drink' as const,
      latitude: inclusion.mapDestinationLocation.latitude,
      longitude: inclusion.mapDestinationLocation.longitude,
    }] : []),
  ], [inclusion.mapDestinationLocation, inclusion.mapOriginLocation])
  const showModal = useContext(ModalContext)
  const openFullScreenMap = useCallback(() => {
    if (!mapMarkers.length) return

    showModal(
      <ExperiencesPassportRouteMapModal
        inclusion={inclusion}
      />,
    )
  }, [inclusion, mapMarkers, showModal])

  if (!inclusion.mapRouteText) return null

  return <MapCard>
    <RouteMapCanvas
      inclusion={inclusion}
      mapHeight={150}
      mapMarkers={mapMarkers}
      staticImageWidth={560}
    />
    <MapFooter>
      <BodyText variant="medium" wrap="truncate">{inclusion.mapRouteText}</BodyText>
      <MapButton type="button" onClick={openFullScreenMap}>
        <Group direction="horizontal" gap={4} verticalAlign="center">
          <BodyText variant="small">
            <FormattedMessage
              id="5iBEYsBy6bNfNY-d6Ds2"
              defaultMessage="Map"
              
            />
          </BodyText>
          <LineArrowUpRightIcon size="XS" />
        </Group>
      </MapButton>
    </MapFooter>
  </MapCard>
}

export default ExperiencesPassportRouteMap
